import { useCallback, useMemo, useState } from 'react'

import { getFractionMixedToImproperChooseQuestionData, FractionModel } from './FractionMixedToImproper.utils'

interface UseFractionImproperToMixedChooseReturnType {
  question?: FractionModel
  options?: FractionModel[]
  formValid: boolean
  selectedAnswer?: number
  handleCheckAnswer: () => void
  onInputChange: (val?: number) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useFractionMixedToImproperChoose({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionImproperToMixedChooseReturnType {
  // eslint-disable-next-line
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const {
    correctAnswerIndex = -1,
    question,
    options,
    maxHistory,
  } = useMemo(
    () => getFractionMixedToImproperChooseQuestionData(difficultyLevel, taskHistory),
    [difficultyLevel, taskHistory],
  )
  const [selectedAnswer, setSelectedAnswer] = useState<number>()

  const handleCheckAnswer = useCallback((): void => {
    if (selectedAnswer === undefined) return
    onAnswerStateChange(selectedAnswer === correctAnswerIndex)
  }, [selectedAnswer, onAnswerStateChange, correctAnswerIndex])

  const onInputChange = useCallback(
    (val?: number) => {
      setSelectedAnswer(val)
      onAnswerStateChange(null)
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [question.numerator, ...prevState].slice(0, maxHistory))
    onInputChange()
    onNextQuestionClick()
  }, [onInputChange, onNextQuestionClick, maxHistory, question])

  return {
    question,
    options,
    formValid: selectedAnswer !== undefined,
    selectedAnswer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default useFractionMixedToImproperChoose
